import { Injectable, inject, signal } from '@angular/core';
import { of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ApiService } from './api.service';
import { AuthService } from './auth.service';

export interface VisitorRecord {
  id: number;
  userId?: number | null;
  userName: string;
  ipAddress?: string | null;
  userAgent?: string | null;
  pagePath?: string | null;
  country?: string | null;
  region?: string | null;
  city?: string | null;
  locationLabel?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  visitedAtUtc: string;
}

export interface VisitorSummary {
  todayVisitors: number;
  todayUniqueIps: number;
  totalVisitors: number;
  thisWeekVisitors: number;
}

@Injectable({ providedIn: 'root' })
export class VisitorService {
  private readonly api = inject(ApiService);
  private readonly auth = inject(AuthService);

  readonly summary = signal<VisitorSummary>({
    todayVisitors: 0,
    todayUniqueIps: 0,
    totalVisitors: 0,
    thisWeekVisitors: 0
  });
  readonly todayVisitors = signal<VisitorRecord[]>([]);
  readonly allVisitors = signal<VisitorRecord[]>([]);
  readonly loading = signal(false);
  readonly listLoading = signal(false);

  /**
   * Record one visit per browser session identity (Guest vs logged-in user).
   * Location is resolved from the browser's public IP so localhost still gets a real city.
   */
  trackVisit(pagePath?: string): void {
    if (typeof sessionStorage === 'undefined') return;
    if (!this.shouldTrackCurrentVisit()) return;

    const user = this.auth.currentUser();
    const identity = user?.UsrId ? `u:${user.UsrId}` : 'guest';
    const key = 'atc_visitor_tracked';
    if (sessionStorage.getItem(key) === identity) return;
    sessionStorage.setItem(key, identity);

    void this.recordVisitAsync(pagePath, user?.UsrId || null, user?.UsrFullName?.trim() || 'Guest');
  }

  private async recordVisitAsync(
    pagePath: string | undefined,
    userId: number | null,
    userName: string
  ): Promise<void> {
    const userAgent = this.currentUserAgent();
    const geo = await this.resolvePublicGeo();
    const body = {
      userId,
      userName,
      pagePath: pagePath || (typeof location !== 'undefined' ? location.pathname : '/'),
      userAgent,
      country: geo?.country || null,
      region: geo?.region || null,
      city: geo?.city || null,
      locationLabel: geo?.locationLabel || null,
      latitude: geo?.latitude ?? null,
      longitude: geo?.longitude ?? null,
      publicIp: geo?.publicIp || null
    };

    this.api.post('/Visitors', body).pipe(catchError(() => of(null))).subscribe();
  }

  /** Prefer IPv4 public IP, then resolve city/region/country. */
  private async resolvePublicGeo(): Promise<{
    country: string | null;
    region: string | null;
    city: string | null;
    locationLabel: string | null;
    latitude: number | null;
    longitude: number | null;
    publicIp: string | null;
  } | null> {
    try {
      const ipv4 = await this.resolvePublicIpv4();
      const geoUrl = ipv4
        ? `https://ipwho.is/${encodeURIComponent(ipv4)}`
        : 'https://ipwho.is/';
      const res = await fetch(geoUrl);
      if (!res.ok) return null;
      const data = await res.json();
      if (data?.success === false) return null;

      const country = (data.country || '').trim() || null;
      const region = (data.region || '').trim() || null;
      const city = (data.city || '').trim() || null;
      const parts = [city, region, country].filter(Boolean);
      const ipFromGeo = this.toIpv4((data.ip || '').trim()) || ipv4;
      return {
        country,
        region,
        city,
        locationLabel: parts.length ? parts.join(', ') : null,
        latitude: typeof data.latitude === 'number' ? data.latitude : null,
        longitude: typeof data.longitude === 'number' ? data.longitude : null,
        publicIp: ipFromGeo
      };
    } catch {
      return null;
    }
  }

  private async resolvePublicIpv4(): Promise<string | null> {
    try {
      const res = await fetch('https://api.ipify.org?format=json');
      if (!res.ok) return null;
      const data = await res.json();
      return this.toIpv4((data?.ip || '').trim());
    } catch {
      return null;
    }
  }

  /** Keep IPv4; unwrap IPv4-mapped IPv6; otherwise null. */
  toIpv4(value?: string | null): string | null {
    const raw = (value || '').trim();
    if (!raw) return null;
    const mapped = raw.match(/^::ffff:(\d{1,3}(?:\.\d{1,3}){3})$/i);
    if (mapped) return mapped[1];
    if (/^\d{1,3}(?:\.\d{1,3}){3}$/.test(raw)) return raw;
    return null;
  }

  loadSummary(): void {
    this.api.get<any>('/Visitors/summary').subscribe({
      next: (raw) => {
        this.summary.set({
          todayVisitors: Number(raw?.todayVisitors ?? raw?.TodayVisitors ?? 0),
          todayUniqueIps: Number(raw?.todayUniqueIps ?? raw?.TodayUniqueIps ?? 0),
          totalVisitors: Number(raw?.totalVisitors ?? raw?.TotalVisitors ?? 0),
          thisWeekVisitors: Number(raw?.thisWeekVisitors ?? raw?.ThisWeekVisitors ?? 0)
        });
      },
      error: () => {
        /* keep previous */
      }
    });
  }

  loadToday(take = 200): void {
    this.loading.set(true);
    this.api.get<any[]>(`/Visitors/today?take=${take}`).subscribe({
      next: (rows) => {
        this.todayVisitors.set(
          Array.isArray(rows)
            ? rows.map((r) => this.normalize(r)).filter((row) => !this.isLikelyBotVisit(row))
            : []
        );
        this.loading.set(false);
      },
      error: () => {
        this.todayVisitors.set([]);
        this.loading.set(false);
      }
    });
  }

  /** Load visitors for a date range (inclusive IST calendar dates → UTC). Omit both for overall. */
  loadList(opts?: { fromDate?: string | null; toDate?: string | null; take?: number }): void {
    this.listLoading.set(true);
    const take = opts?.take ?? 500;
    const params: string[] = [`take=${take}`];

    const fromUtc = this.istDateStartToUtcIso(opts?.fromDate);
    const toUtc = this.istDateEndExclusiveToUtcIso(opts?.toDate);
    if (fromUtc) params.push(`fromUtc=${encodeURIComponent(fromUtc)}`);
    if (toUtc) params.push(`toUtc=${encodeURIComponent(toUtc)}`);

    this.api.get<any[]>(`/Visitors?${params.join('&')}`).subscribe({
      next: (rows) => {
        this.allVisitors.set(
          Array.isArray(rows)
            ? rows.map((r) => this.normalize(r)).filter((row) => !this.isLikelyBotVisit(row))
            : []
        );
        this.listLoading.set(false);
      },
      error: () => {
        this.allVisitors.set([]);
        this.listLoading.set(false);
      }
    });
  }

  loadOverview(): void {
    this.loadSummary();
    this.loadToday(200);
  }

  /** yyyy-MM-dd interpreted as IST midnight → UTC ISO. */
  private istDateStartToUtcIso(dateYmd?: string | null): string | null {
    const raw = (dateYmd || '').trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) return null;
    // IST = UTC+05:30 → midnight IST is 18:30 previous day UTC
    return new Date(`${raw}T00:00:00+05:30`).toISOString();
  }

  private istDateEndExclusiveToUtcIso(dateYmd?: string | null): string | null {
    const raw = (dateYmd || '').trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) return null;
    const start = new Date(`${raw}T00:00:00+05:30`);
    start.setTime(start.getTime() + 24 * 60 * 60 * 1000);
    return start.toISOString();
  }

  isLikelyBotVisit(record: Pick<VisitorRecord, 'userAgent' | 'userName' | 'pagePath'>): boolean {
    const userAgent = (record.userAgent || '').trim();
    const userName = (record.userName || '').trim();
    const pagePath = (record.pagePath || '').trim().toLowerCase();

    if (this.isLikelyBotUserAgent(userAgent)) return true;
    if (!userAgent && /^bot$/i.test(userName)) return true;
    if (pagePath === '/robots.txt' || pagePath === '/sitemap.xml') return true;
    return false;
  }

  private shouldTrackCurrentVisit(): boolean {
    if (typeof navigator === 'undefined') return false;
    if (navigator.webdriver) return false;
    return !this.isLikelyBotUserAgent(this.currentUserAgent());
  }

  private currentUserAgent(): string {
    return typeof navigator !== 'undefined' ? (navigator.userAgent || '').trim() : '';
  }

  private isLikelyBotUserAgent(userAgent?: string | null): boolean {
    const ua = (userAgent || '').trim().toLowerCase();
    if (!ua) return false;
    return /(bot|crawler|crawl|spider|slurp|preview|headless|phantom|lighthouse|pagespeed|facebookexternalhit|whatsapp|monitor|archive|wget|curl|python-requests|python-urllib|axios|node-fetch|go-http-client|java\/|okhttp|apache-httpclient|libwww-perl|scrapy|selenium|playwright|puppeteer|postmanruntime|insomnia)/i.test(
      ua
    );
  }

  private normalize(source: any): VisitorRecord {
    return {
      id: Number(source.id ?? source.Id ?? 0),
      userId: source.userId ?? source.UserId ?? null,
      userName: source.userName ?? source.UserName ?? 'Guest',
      ipAddress: source.ipAddress ?? source.IpAddress ?? null,
      userAgent: source.userAgent ?? source.UserAgent ?? null,
      pagePath: source.pagePath ?? source.PagePath ?? null,
      country: source.country ?? source.Country ?? null,
      region: source.region ?? source.Region ?? null,
      city: source.city ?? source.City ?? null,
      locationLabel: source.locationLabel ?? source.LocationLabel ?? null,
      latitude: source.latitude ?? source.Latitude ?? null,
      longitude: source.longitude ?? source.Longitude ?? null,
      visitedAtUtc: String(source.visitedAtUtc ?? source.VisitedAtUtc ?? '')
    };
  }
}
